import { useEffect, useState } from "react";
import { ArrowRight, Ticket, HandHeart, GraduationCap, MapPin, Calendar, Award } from "lucide-react";
import { EVENTBRITE_URL, ZEFFY } from "../../lib/api";

const SLIDES = [
  {
    url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/kwje9279_IMG_2922.jpeg",
    label: "Red Carpet · 2023 Gala",
    real: true,
  },
  {
    url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/qiqmz614_IMG_1085.jpeg",
    label: "Keynote Moment · 2023 Gala",
    real: true,
  },
  {
    url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/5yc8p4g9_IMG_2480.jpg",
    label: "Ballroom · 2023 Gala",
    real: true,
  },
  {
    url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/l6x6ptrm_IMG_2959.jpeg",
    label: "Fine Dining · 2023 Gala",
    real: true,
  },
  {
    url: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1920&q=80",
    label: "Executive Networking",
  },
  {
    url: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&q=80",
    label: "Women Leading the Industry",
  },
  {
    url: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920&q=80",
    label: "Scholarship Recipients",
  },
  {
    url: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80",
    label: "Transportation Industry",
  },
];

const EVENT_DATE = new Date("2026-12-10T18:00:00-05:00");

function getCountdown() {
  const diff = Math.max(0, EVENT_DATE.getTime() - Date.now());
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);
  return { days, hours, minutes, seconds };
}

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const [countdown, setCountdown] = useState(getCountdown());

  useEffect(() => {
    const id = setInterval(() => setIdx((p) => (p + 1) % SLIDES.length), 6500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCountdown(getCountdown()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative h-screen min-h-[720px] w-full overflow-hidden bg-aawta-jet"
    >
      {/* Slides */}
      {SLIDES.map((s, i) => (
        <div
          key={s.url}
          className={`absolute inset-0 transition-opacity duration-[1800ms] ease-out ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.url}
            alt={s.label}
            className={`w-full h-full object-cover ${i === idx ? "animate-ken-burns" : ""}`}
            key={`${s.url}-${idx === i}`}
          />
        </div>
      ))}

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
      <div className="absolute inset-0 noise-overlay" />

      {/* Side rail label */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3 z-10">
        <div className="h-24 w-px bg-white/30" />
        <p className="text-[10px] tracking-[0.4em] text-white/60 rotate-180" style={{ writingMode: "vertical-rl" }}>
          DECATUR, GA · DEC 10, 2026
        </p>
        <div className="h-24 w-px bg-white/30" />
      </div>

      {/* Slide indicator */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-2 z-10">
        {SLIDES.map((s, i) => (
          <button
            key={`dot-${s.url}`}
            onClick={() => setIdx(i)}
            aria-label={`slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`w-1 transition-all duration-500 ${
              i === idx ? "h-10 bg-aawta-pink" : "h-4 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-20 lg:pb-28 px-6 md:px-16 lg:px-24 max-w-[1500px] mx-auto">
        <div className="max-w-4xl">
          <p data-testid="hero-eyebrow" className="text-overline text-aawta-pink mb-6">
            3RD ANNUAL · EXCLUSIVE EVENING · LIMITED TO 50 INVITED GUESTS
          </p>
          <h1
            data-testid="hero-title"
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] font-light text-white tracking-tighter mb-6"
          >
            Power to <em className="text-aawta-pink not-italic font-normal">Drive Change</em>
          </h1>
          <p data-testid="hero-subtitle" className="text-lg md:text-xl text-white/80 max-w-2xl mb-6 leading-relaxed">
            <span className="text-white font-medium">Workforce, Leadership &amp; Industry Impact Reception.</span>{" "}
            Celebrating the leaders, organizations, and communities driving change throughout trucking,
            logistics, transportation, and supply chain.
          </p>

          <div data-testid="hero-event-meta" className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85 mb-4">
            <span className="inline-flex items-center gap-2">
              <Calendar size={14} className="text-aawta-pink" />
              Thursday, December 10, 2026
            </span>
            <span className="hidden sm:inline text-white/30">·</span>
            <span className="inline-flex items-center gap-2">
              <MapPin size={14} className="text-aawta-pink" />
              Decatur, Georgia
            </span>
          </div>

          <div
            data-testid="hero-partnership"
            className="inline-flex items-center gap-2 border-l-2 border-aawta-pink pl-3 py-1 mb-6"
          >
            <span className="text-overline text-aawta-pink">IN PARTNERSHIP WITH</span>
            <span className="text-sm text-white/85">
              Courtyard by Marriott Atlanta Decatur Downtown/Emory
            </span>
          </div>

          {/* Countdown */}
          <div data-testid="hero-countdown" className="flex items-center gap-2 sm:gap-3 mb-8">
            <CountBox label="Days"    value={countdown.days} testId="count-days" />
            <span className="font-display text-aawta-pink text-2xl">:</span>
            <CountBox label="Hours"   value={countdown.hours} testId="count-hours" />
            <span className="font-display text-aawta-pink text-2xl">:</span>
            <CountBox label="Minutes" value={countdown.minutes} testId="count-minutes" />
            <span className="font-display text-aawta-pink text-2xl hidden sm:inline">:</span>
            <div className="hidden sm:block">
              <CountBox label="Seconds" value={countdown.seconds} testId="count-seconds" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <a
              data-testid="hero-tickets-btn"
              href={EVENTBRITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pink"
            >
              <Ticket size={16} /> PURCHASE TICKETS <ArrowRight size={14} />
            </a>
            <a
              data-testid="hero-sponsor-btn"
              href={ZEFFY.sponsor}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-blue"
            >
              <HandHeart size={16} /> BECOME A SPONSOR
            </a>
            <a
              data-testid="hero-nominations-btn"
              href="#awards"
              className="btn-ghost"
            >
              <Award size={16} /> 2026 NOMINATIONS NOW OPEN
            </a>
            <a
              data-testid="hero-scholarship-btn"
              href={ZEFFY.scholarship}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
            >
              <GraduationCap size={16} /> FUND A SCHOLARSHIP
            </a>
          </div>

          <div className="mt-12 flex items-center gap-6 text-xs tracking-[0.2em] uppercase text-white/50">
            <span className="text-white/80 flex items-center gap-3">
              {SLIDES[idx].real && (
                <span data-testid="hero-real-badge" className="inline-flex items-center gap-1.5 bg-aawta-pink/15 text-aawta-pink border border-aawta-pink/40 px-2 py-0.5 text-[10px]">
                  <span className="w-1.5 h-1.5 bg-aawta-pink rounded-full animate-pulse" />
                  LIVE FROM 2024
                </span>
              )}
              {SLIDES[idx].label}
            </span>
            <span className="h-px flex-1 bg-white/15 max-w-xs" />
            <span>{String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function CountBox({ label, value, testId }) {
  const str = String(Math.max(0, value)).padStart(2, "0");
  return (
    <div className="flex flex-col items-center">
      <div
        data-testid={testId}
        className="min-w-[56px] sm:min-w-[64px] bg-aawta-pink/10 border border-aawta-pink/40 text-aawta-pink font-display text-2xl sm:text-3xl tracking-wider px-3 py-2 text-center backdrop-blur-sm"
      >
        {str}
      </div>
      <span className="text-[10px] tracking-[0.3em] text-white/60 mt-1.5">{label.toUpperCase()}</span>
    </div>
  );
}
